import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useAuth } from "@/lib/AuthContext";
import { useTenant } from "@/lib/TenantContext";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { api } from "@/lib/api";
import { tenantHref } from "@/lib/tenantPath";
import { finalizeBridgeIfPending } from "@/lib/authBridge";
import ThemedPageLayout from "@/templates/ThemedPageLayout";
import { Loader2, Calendar, GraduationCap, Video, LogOut, ArrowRight, ReceiptText, MessageSquare } from "lucide-react";
import ClientAuthPanel from "@/components/auth/ClientAuthPanel";
// Chemin RELATIF volontaire (cf. lib/LanguageContext.jsx) : "@/" n'est pas mappé côté
// Jest, un chemin relatif se résout nativement dans un futur test non mocké.
import { fmtMoney } from "../admin/money";
import { resolveBrandName } from "../lib/siteLinks";
import defaultGatewayApi from "../gateway/api";
import { useConsultStatus } from "../gateway/hooks/useConsultStatus";
// The consultation's own vocabulary, mirrored from `schemas.SESSION_STATES` —
// imported rather than re-listed so a state the backend adds cannot reach this
// screen as a raw identifier (`i18n/__tests__/keyCoverage.test.js` pins the labels).
import { SESSION_STATES, SESSION_MONEY_FINAL_STATES, isFixedBlock, isPaidFixedBlock } from "../gateway/contract";
import ClientConversation from "./ClientConversation";
import ConsultJoinButton from "./ConsultJoinButton";
import ConsultReauthPanel from "./ConsultReauthPanel";
// The join window's `opensAt` is an instant, not a translatable string. ONE formatter,
// exported by the room frame, so the sentence on this row and the sentence inside the
// room state the same moment in the same shape.
import { fmtWhen } from "./ConsultRoomFrame";
import { REVIEWS } from "../constants/testIds/reviews";
import Section from "./PortalSection";
import PortalPathways from "./pathways/PortalPathways";
import PortalEmailPreferences from "./PortalEmailPreferences";

const fmtDate = (val, lang) => {
  if (!val) return "";
  try {
    return new Date(val).toLocaleString(lang === "fr" ? "fr-CA" : lang === "es" ? "es-ES" : "en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(val);
  }
};

/**
 * Where the portal offers "reschedule / cancel".
 *
 * `pending_payment` JOINED THE LIST ON 2026-09-03 (finding F-11). A per-minute
 * consultation sits in that status from the moment it is booked until its authorisation
 * lands, so the patient who abandoned a Stripe Checkout could neither finish paying NOR
 * give the slot back from their own area — the seat stayed held until the sweeper took
 * it. `POST /api/bookings/manage/cancel` has always accepted it (it expires the pending
 * Checkout session as part of the cancel), so this list was the only thing in the way.
 *
 * The terminal statuses are absent for the reason they always were: there is nothing left
 * to manage. `paid_needs_attention` is absent too — that booking is waiting on a HUMAN
 * decision at the practice, and letting the client cancel around it would decide it for
 * them.
 */
export const MANAGEABLE_BOOKING_STATUSES = ["pending", "confirmed", "pending_payment"];

// `authState` alone is never the join gate (gateway-api-contract.md §2: it answers only
// the money question and stays "authorised" long after a consult is over) — this maps it
// to the disabled label ONLY for the pre-authorisation wait, which is all a `!canJoin`
// with a non-terminal session ever means here.
//
// EVERY REASON HERE IS ABOUT A CONSULTATION STILL AHEAD (F-25 residue R1, 2026-09-03).
// `none` is the fall-through, and it says "not available YET" — a sentence that is only
// true while there is something to wait for. A SETTLED consultation never reaches this
// map: `ConsultRoomState` returns before the join control exists at all (see below), so
// the outcome is stated once, by the pill, and no line beside it contradicts it. The
// screen this replaced showed "Completed and billed" and "The join link isn't available
// yet." side by side, on the same page as that consultation's own receipt.
//
// If a settled outcome ever wants a sentence of its own, it gets its OWN key per outcome
// (fr/en/es, `keyCoverage.test.js`) — "the room closed without a call", "no charge was
// made" — and never this map's "yet", which would be a statement about the future made
// about something that is over.
const JOIN_REASON_KEYS = {
  none: "consult.join.reasonNone",
  awaiting: "consult.join.reasonAwaiting",
  failed: "consult.join.reasonFailed",
};

/**
 * The consultation's own state and door, addressed by the APPOINTMENT it belongs to
 * (`gateway-flow-c-design.md` §6.3: "Frontend renders the consult join button from the
 * appointment", not from the room). `sessionStatus` is what `/client/me` stated at load;
 * the poll below keeps it current. Polls the same patient-safe status endpoint
 * `ConsultRoomFrame` does (`GET /consults/{id}/status`, never the owner-only session
 * route) so the button unlocks itself the moment authorisation lands, without the client
 * having to reload this page.
 *
 * B4 (contract §2, AMENDED 2026-08-27): `authState: "failed"` on the scheduled (>144h)
 * lane means the T-48h off-session authorisation was declined or the card expired — the
 * door will not open until a NEW authorisation lands. Before this, the row degraded to a
 * disabled button captioned "check your email", with no way to act from here at all. This
 * surfaces the same re-auth path `ConsultBooking.jsx` used for the original booking —
 * `POST /consults/{id}/intents` re-invoked — directly on the row that would otherwise
 * look joinable.
 */
const ConsultRoomState = ({ appointmentId, token, sessionStatus: propSessionStatus, labels }) => {
  const t = useT("public");
  const { lang } = useLanguage();
  const { data: status } = useConsultStatus(appointmentId, token, { poll: true }, defaultGatewayApi);

  // THE CONSULTATION'S STATE, FROM THE CONSULTATION (F-25). Two transports for ONE fact:
  // `/client/me` states it at page load (`consult_session_status`, now carried on the
  // appointment's own consult row), and this poll — which the join control needs anyway —
  // keeps it current while the row is on screen. The `video_rooms.status` this used to
  // render is a literal written once at provisioning and never moved, which is how a
  // consult that had been held, billed and receipted went on reading "Scheduled" for ever.
  const sessionStatus = status?.sessionStatus || propSessionStatus || "";

  // A SETTLED CONSULTATION HAS NO DOOR, AND NEEDS NO EXCUSE FOR NOT HAVING ONE (F-25
  // residue R1). The money is final: there is nothing left to join, nothing left to
  // authorise, and the pill already says what happened — captured, no-show, cancelled —
  // in that outcome's own words. Anything rendered beside it could only be a second,
  // weaker account of the same fact, which is how "The join link isn't available yet."
  // ended up under "Completed and billed".
  //
  // BEFORE the `authState: "failed"` branch, deliberately: re-authorisation is an offer
  // to pay for a consultation that is still to come, and this one is over.
  //
  // The gate is the session, NOT `joinState: "closed"` — those are different facts. A
  // window that has shut over a consultation whose money is STILL in flight leaves a
  // patient owed an explanation ("this consultation's room has closed, contact the
  // practice"), and that sentence stays: it says nothing about a future, and the pill
  // there says nothing about an outcome.
  if (SESSION_MONEY_FINAL_STATES.includes(sessionStatus)) {
    return <StatusPill status={sessionStatus} labels={labels} />;
  }

  // THE SAME RULE FOR A FIXED BLOCK, WHOSE "SETTLED" IS A DIFFERENT FACT. It never opens a
  // session, so `sessionStatus` is permanently "" and the check above can never fire for
  // one — the row would go on offering a join button for a consultation delivered last
  // month. What is final for this model is the APPOINTMENT: `completed` is written when
  // the window closes, and there is nothing left to join, pay or explain.
  if (isFixedBlock(status) && status?.bookingStatus === "completed") {
    return (
      <span
        className="text-xs px-2.5 py-1 rounded-full bg-eh-cream text-eh-muted"
        data-testid={`consult-block-completed-${appointmentId}`}
      >
        {t("consult.join.blockCompleted")}
      </span>
    );
  }

  const authFailed = status?.authState === "failed";

  if (authFailed) {
    return (
      <>
        {sessionStatus && <StatusPill status={sessionStatus} labels={labels} />}
        <ConsultReauthPanel appointmentId={appointmentId} token={token} cancelPath="/compte" />
      </>
    );
  }

  // WHY THE REASON IS NOT `authState` ALONE (F-15). An authorised consultation whose room
  // has not opened yet is `authState: "authorised"` and `canJoin: false`, which fell
  // through this map to "the join link isn't available yet" — true, and useless: the
  // patient has paid and wants to know WHEN. `joinState` is the server's answer to the
  // other question, and the not-yet-open case states the instant instead.
  const notYetOpen = status?.joinState === "not_yet_open";
  // A FIXED BLOCK HAS NO AUTHORISATION TO BE WAITING FOR, so the `authState` map — whose
  // every sentence is about a card hold — would tell a patient whose checkout is simply
  // unfinished that we are waiting on their authorisation. The only reason a paid-upfront
  // consultation is shut inside its own window is that it has not been paid for.
  const reasonKey = status?.joinState === "closed"
    ? "consult.join.reasonClosed"
    : isFixedBlock(status)
      ? "consult.join.reasonNotPaid"
      : (JOIN_REASON_KEYS[status?.authState] || JOIN_REASON_KEYS.none);

  // MONEY BEFORE TIME, and never a money claim the money has not earned. `reasonNotYetOpen`
  // opens with "Payment authorised" — true of every per-minute consultation that reaches
  // this list, because one is listed only once a hold exists. A FIXED BLOCK is listed from
  // the moment it is booked, so an abandoned checkout landed here and was told its payment
  // was authorised, on the same screen whose appointment row said "Awaiting payment".
  //
  // So the not-yet-open sentence is chosen by WHO IS ASKING: an unpaid block never sees a
  // time answer at all (the money answer is the one it can act on, exactly as the server's
  // own gate order has it), and a paid block gets the instant with no claim attached.
  const blockPaid = isPaidFixedBlock(status);
  const timeAnswerAllowed = notYetOpen && status?.opensAt && (!isFixedBlock(status) || blockPaid);
  const notYetOpenKey = isFixedBlock(status)
    ? "consult.join.blockNotYetOpen"
    : "consult.join.reasonNotYetOpen";

  return (
    <>
      {sessionStatus && <StatusPill status={sessionStatus} labels={labels} />}
      <ConsultJoinButton
        appointmentId={appointmentId}
        token={token}
        canJoin={Boolean(status?.canJoin)}
        reasonKey={timeAnswerAllowed ? undefined : reasonKey}
        reasonText={timeAnswerAllowed
          ? t(notYetOpenKey, { when: fmtWhen(status.opensAt, lang) })
          : undefined}
      />
    </>
  );
};

const StatusPill = ({ status, labels }) => {
  const map = {
    pending: "bg-amber-100 text-amber-800",
    confirmed: "bg-emerald-100 text-emerald-800",
    scheduled: "bg-sky-100 text-sky-800",
    active: "bg-emerald-100 text-emerald-800",
    completed: "bg-slate-100 text-slate-600",
    cancelled: "bg-rose-100 text-rose-700",
    pending_payment: "bg-amber-100 text-amber-800",
    paid_needs_attention: "bg-amber-100 text-amber-800",
    // The CONSULTATION vocabulary (`SESSION_STATES`), rendered by the same pill on the
    // consults section. Slate for the three states in which the money is settled — the
    // same "this is history now" reading `completed` has above — and sky/emerald for the
    // two that are still live. `scheduled` is shared with the booking vocabulary above
    // and means the same thing in both.
    in_progress: "bg-emerald-100 text-emerald-800",
    computing: "bg-sky-100 text-sky-800",
    computed: "bg-sky-100 text-sky-800",
    captured: "bg-slate-100 text-slate-600",
    void_no_show: "bg-slate-100 text-slate-600",
  };
  return (
    <span data-testid={`status-pill-${status}`} className={`text-xs px-2.5 py-1 rounded-full font-medium ${map[status] || "bg-slate-100 text-slate-600"}`}>
      {labels[status] || status}
    </span>
  );
};

export default function ClientPortal() {
  const { user, loading: authLoading, logout, refresh } = useAuth();
  // The Messages section's counter. It is owned by the THREAD, not by `GET /client/me`:
  // the conversation polls on its own 60 s clock, so the portal cannot know the number at
  // fetch time and must be told. `useState`'s setter is referentially stable, so handing
  // it straight to the child costs no re-render loop.
  const [messageCount, setMessageCount] = useState(0);
  const { tenant } = useTenant();
  const { lang } = useLanguage();
  const t = useT("public");
  // "shared.brand.fallback" lives in the "templates" dictionary (the one every other
  // brand surface — Navbar, Hero, Footer, useSiteData — already reads it from), not in
  // "public"; useT()'s namespace lookup does not cross dictionaries (i18n/index.js
  // `lookup`), so a second, differently-namespaced `t` is needed for that one key.
  const tt = useT("templates");
  const statusLabels = useMemo(() => ({
    pending: t("portal.status.pending"),
    confirmed: t("portal.status.confirmed"),
    cancelled: t("portal.status.cancelled"),
    scheduled: t("portal.status.scheduled"),
    active: t("portal.status.active"),
    completed: t("portal.status.completed"),
    // Money-in-flight states. `StatusPill` falls back to `|| status`, so any status
    // missing from this map is printed to the CUSTOMER as the raw column value — that is
    // how "pending_payment" reached them, in all three languages. Every status in
    // modules/booking/schemas.LIVE_BOOKING_STATUSES can land on a client's own row, and
    // i18n/__tests__/keyCoverage.test.js now asserts that set against this key family.
    pending_payment: t("portal.status.pending_payment"),
    paid_needs_attention: t("portal.status.paid_needs_attention"),
  }), [t]);
  // The CONSULTATION's own labels, built from the backend's state tuple rather than
  // spelled out here (F-25): a state added to `SESSION_STATES` without a label would
  // otherwise reach the client as a raw identifier through `StatusPill`'s `|| status`
  // fallback, which is exactly how "pending_payment" once reached them in three
  // languages. `keyCoverage.test.js` refuses the pull request that adds one without a
  // translation.
  const consultStatusLabels = useMemo(
    () => Object.fromEntries(
      SESSION_STATES.map((s) => [s, t(`portal.consultStatus.${s}`)])),
    [t]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/client/me");
      setData(data);
    } catch (e) {
      console.warn("[portal] load failed", e?.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading) load();
  }, [authLoading, load]);

  // ClientAuthPanel already calls refresh() itself (Google and OTP paths alike)
  // before invoking onSuccess, and this component already reacts to `user` (see
  // useEffect/load above) — no navigation or extra state update is needed here.
  const handleAuthSuccess = () => {};

  // ── Google auth bridge (tenant hosts) ───────────────────────────────────────
  // GIS only accepts REGISTERED origins: on a tenant host, sign-in leaves for
  // {platformOrigin}/auth/bridge, which replays GIS on the platform origin, then
  // returns here with ?hb_finalize=1 (see lib/authBridge.js). This component stays
  // the only one that renders the bridge error ("expired"/"invalid") — the other
  // pages going through the same bridge (useRequireAuth.js) have no dedicated screen.
  const [authError, setAuthError] = useState("");
  // Single-execution guard: the hb_code is single-use (find_one_and_delete), and
  // React StrictMode double-invokes this effect in dev — two POST /finalize calls
  // would race (one with the nonce already consumed), and one would cancel the other.
  const finalizedRef = useRef(false);
  useEffect(() => {
    if (finalizedRef.current) return;
    finalizedRef.current = true;
    finalizeBridgeIfPending(refresh).then((result) => {
      if (result.status === "error") setAuthError(result.reason);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // BUG-226: the practice's public NAME, not its tagline — same resolution as every
  // other brand surface in the product (Navbar/Hero/Footer/useSiteData on the frontend,
  // `backend/emails/shared.py::_tenant_brand` on the backend): name, then owner_name,
  // then a localized placeholder. The tagline never enters this chain; it is marketing
  // copy for the homepage hero, not an identity a client replying to a message can use.
  const practiceName = resolveBrandName(tenant, tt("shared.brand.fallback"));

  if (authLoading || loading) {
    return (
      <ThemedPageLayout>
        <section className="pt-32 pb-24 flex items-center justify-center">
          <Loader2 className="animate-spin text-eh-sage-deep" size={28} />
        </section>
      </ThemedPageLayout>
    );
  }

  // --- Not authenticated: show login ---
  // Inside the chrome like every other state: a visitor who lands on My Account by mistake,
  // or who cannot sign in, must still be able to reach the practice's site from here.
  // The card below is a LIGHT surface (cream/ink) that stays light on every template, and
  // ClientAuthPanel inside it declares the same palette for itself (BUG-047). It has to:
  // this embedding sits INSIDE TemplateShell's [data-tpl] scope, so anything the panel took
  // from `var(--tpl-text)` would be the theme's colour — cream type on this cream card on
  // the dark templates, which is how the whole sign-in gate went invisible on t4 Bold.
  if (!user) {
    return (
      <ThemedPageLayout>
        <section className="pt-32 pb-24 px-6 flex items-center justify-center">
          <div data-testid="client-login-card" className="max-w-md w-full bg-eh-cream rounded-3xl border border-eh-border-soft p-10 shadow-[0_24px_60px_-25px_rgba(44,53,49,0.2)] text-center">
            <h1 className="font-display text-3xl text-eh-ink leading-tight">{t("portal.title")}</h1>
            <p className="text-eh-muted mt-3">{t("portal.signinPrompt")}</p>
            {authError && (
              <p data-testid="client-portal-bridge-error" className="mt-4 text-sm text-red-600">
                {authError === "expired" ? t("portal.authErrExpired") : t("portal.authErrInvalid")}
              </p>
            )}
            <div className="mt-8">
              <ClientAuthPanel onSuccess={handleAuthSuccess} />
            </div>
          </div>
        </section>
      </ThemedPageLayout>
    );
  }

  const bookings = data?.bookings || [];
  const enrollments = data?.enrollments || [];
  // THE CONSULTATIONS ARE APPOINTMENTS, NOT ROOMS (QA-22). This used to be `data.rooms`,
  // the `video_rooms` door rows — and a door is written only when somebody OPENS the
  // room, so a patient who paid and closed the tab saw nothing here of the consultation
  // they were holding a live authorisation for. The server now publishes the union
  // itself (`consults.client_consult_rows`): one row per consult APPOINTMENT, carrying
  // the `manage_token` the join authenticates with and whatever session state exists.
  const consults = data?.consults || [];
  const receipts = data?.receipts || [];

  return (
    <ThemedPageLayout>
      <section className="pt-32 pb-20 max-w-5xl mx-auto px-6">
        {/* Page heading: who is signed in, and how to sign out. PAGE CONTENT, not a second
            site header — the practice's own header and footer are the layout's. */}
        <div className="flex flex-wrap items-center gap-4 mb-10" data-testid="client-portal-header">
          {user.picture ? (
            <img src={user.picture} alt="" className="w-14 h-14 rounded-full object-cover border border-eh-border-soft" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-eh-sage/20 flex items-center justify-center font-display text-xl text-eh-sage-deep">
              {(user.name || user.email || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <h1 className="font-display text-3xl md:text-4xl text-eh-ink leading-tight">{t("portal.title")}</h1>
            <p className="text-eh-muted text-sm mt-1">{user.name || user.email} · {practiceName}</p>
          </div>
          <button
            onClick={logout}
            data-testid="client-logout"
            className="ml-auto inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border border-eh-border-soft text-eh-muted hover:text-eh-ink hover:border-eh-ink/30 transition"
          >
            <LogOut size={15} /> {t("portal.logout")}
          </button>
        </div>

        <div className="space-y-6">
          {/* Bookings */}
          <Section icon={Calendar} title={t("portal.bookings")} count={bookings.length} testid="client-bookings">
            {bookings.length === 0 ? (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-eh-muted">{t("portal.noBookings")}</p>
                <a href={tenantHref("/reserver")} data-testid="client-book-cta" className="inline-flex items-center gap-2 text-sm font-medium text-eh-sage-deep hover:underline">
                  {t("portal.bookNow")} <ArrowRight size={15} />
                </a>
              </div>
            ) : (
              <ul className="divide-y divide-eh-border-soft">
                {bookings.map((b, i) => (
                  <li key={b.id || i} className="py-3 flex items-center justify-between gap-3" data-testid={`booking-row-${i}`}>
                    <div>
                      <p className="font-medium">{b.service || t("portal.consultationFallback")}</p>
                      <p className="text-sm text-eh-muted">{fmtDate(b.slot_start, lang)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* THE WAY BACK TO AN ABANDONED AUTHORISATION (F-11). A patient who
                          closed the Stripe tab on a per-minute consultation had this row,
                          an "Awaiting payment" pill and nothing else: the page that
                          finishes the job — `/consultation/confirmer`, which re-opens the
                          same Checkout session — was a URL no surface and no mail ever
                          handed them.

                          `consult_can_authorise` is the SERVER's decision
                          (`consults.can_resume_authorisation`), not a status this screen
                          re-interprets: whether a second Checkout is the right next step
                          is a money question, and a booking status cannot answer it (a
                          `confirmed` consult whose card was declined needs this link; one
                          whose card is saved for the T-48h job must never get it). The
                          capability spent on that page is the `manage_token` this row
                          already carries. */}
                      {b.consult_can_authorise && b.manage_token && (
                        <a
                          href={tenantHref(`/consultation/confirmer?appointmentId=${encodeURIComponent(b.id)}&token=${encodeURIComponent(b.manage_token)}`)}
                          data-testid={`booking-authorise-${i}`}
                          className="text-sm font-medium text-eh-sage-deep hover:underline"
                        >
                          {t("portal.authoriseConsult")}
                        </a>
                      )}
                      {/* Self-service cancel/reschedule (public/ManageBooking.jsx). `manage_token`
                          only ever appears on a live, still-actionable booking (server-side, see
                          /api/client/me) — restricting to the manageable statuses here is a
                          UI-level match of that intent, not a second source of truth. */}
                      {b.manage_token && MANAGEABLE_BOOKING_STATUSES.includes(b.status) && (
                        <a
                          href={tenantHref(`/gerer-rdv?token=${encodeURIComponent(b.manage_token)}`)}
                          data-testid={`booking-manage-${i}`}
                          className="text-sm font-medium text-eh-sage-deep hover:underline"
                        >
                          {t("portal.manageBooking")}
                        </a>
                      )}
                      {/* THE REVIEW INVITATION, straight off the booking row. The server
                          attaches `review: {state, review_url, expires_at} | null` to each
                          booking of THIS client, and puts the token inside `review_url`
                          and only while the request is `pending` — so this screen never
                          holds a capability it is not currently offering. An ABSOLUTE URL
                          from the server, therefore a plain <a> and not `tenantHref`: the
                          form's origin is the practice's, which the server states and the
                          browser must not re-derive. `null` (no request, no-show, program
                          paused, an older backend) renders nothing at all. */}
                      {b.review?.state === "pending" && b.review.review_url && (
                        <a
                          href={b.review.review_url}
                          data-testid={REVIEWS.portalCta(b.id)}
                          className="text-sm font-medium text-eh-sage-deep hover:underline"
                        >
                          {t("portal.leaveReview")}
                        </a>
                      )}
                      {b.review?.state === "submitted" && (
                        <span
                          data-testid={REVIEWS.portalDone(b.id)}
                          className="text-sm text-eh-muted"
                        >
                          {t("portal.reviewed")}
                        </span>
                      )}
                      <StatusPill status={b.status} labels={statusLabels} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Courses */}
          <Section icon={GraduationCap} title={t("portal.courses")} count={enrollments.length} testid="client-courses">
            {enrollments.length === 0 ? (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-eh-muted">{t("portal.noCourses")}</p>
                <a href={tenantHref("/learn")} data-testid="client-courses-cta" className="inline-flex items-center gap-2 text-sm font-medium text-eh-sage-deep hover:underline">
                  {t("portal.browseCourses")} <ArrowRight size={15} />
                </a>
              </div>
            ) : (
              <ul className="grid sm:grid-cols-2 gap-4">
                {enrollments.map((e, i) => (
                  <li key={e.course_id || i} className="rounded-2xl border border-eh-border-soft overflow-hidden bg-white/40" data-testid={`enrollment-row-${i}`}>
                    {e.course_cover && <img src={e.course_cover} alt="" className="w-full h-28 object-cover" />}
                    <div className="p-4">
                      <p className="font-medium leading-snug">{e.course_title || t("portal.courseFallback")}</p>
                      <p className="text-xs text-eh-muted mt-1">
                        {e.completed_count || 0}/{e.lesson_count || 0} {t("portal.lessonsDone")}
                      </p>
                      <a
                        href={tenantHref(`/learn/${e.course_id}`)}
                        data-testid={`enrollment-continue-${i}`}
                        className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-eh-sage-deep hover:underline"
                      >
                        {t("portal.continue")} <ArrowRight size={14} />
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Learning pathways the client is enrolled in. The section reads its own data
              (GET /client/pathways) rather than `/client/me`'s, so a failure there is
              stated inside it and never costs the client the rest of this page. */}
          <PortalPathways />

          {/* Purchases & tax receipts — absence-tolerant: no `receipts` array (older
              backend) or an empty one both render nothing, exactly today's UI. */}
          {receipts.length > 0 && (
            <Section icon={ReceiptText} title={t("portal.receipts")} count={receipts.length} testid="client-receipts">
              <ul className="divide-y divide-eh-border-soft">
                {receipts.map((r, i) => (
                  <li key={r.receipt_number || i} className="py-3 flex items-center justify-between gap-3" data-testid={`receipt-row-${i}`}>
                    <div>
                      <p className="font-medium">{r.description || t("portal.consultationFallback")}</p>
                      <p className="text-sm text-eh-muted">
                        <span className="font-mono">{r.receipt_number}</span> · {fmtDate(r.issued_at, lang)}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-medium">{fmtMoney(r.total_cents, r.currency)}</span>
                      <a
                        href={r.view_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`receipt-view-${i}`}
                        className="text-sm font-medium text-eh-sage-deep hover:underline"
                      >
                        {t("portal.receiptView")}
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Video consultations */}
          {consults.length > 0 && (
            <Section icon={Video} title={t("portal.consults")} count={consults.length} testid="client-consults">
              <ul className="divide-y divide-eh-border-soft">
                {consults.map((c, i) => (
                  <li key={c.appointment_id || i} className="py-3 flex items-center justify-between gap-3" data-testid={`consult-row-${i}`}>
                    <div>
                      <p className="font-medium">{c.service || t("portal.consultationFallback")}</p>
                      <p className="text-sm text-eh-muted">{fmtDate(c.slot_start, lang)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {/* THE DOOR IS THE APPOINTMENT'S (gateway-flow-c-design.md §6.3:
                          "Frontend renders the consult join button from the
                          appointment"), and the row IS the appointment now — no lookup
                          into `bookings[]` and no room document in between. The gate is
                          the `manage_token` the row carries: without one there is nothing
                          to authenticate the join or the status poll with, so the row
                          degrades to the state alone — never a fabricated token.

                          THE PILL IS INSIDE (F-25): the state and the door are one
                          component's answer, from one document. The screen this replaced
                          rendered the door row's frozen "scheduled" beside a join control
                          that knew the consultation was over. */}
                      {c.manage_token ? (
                        <ConsultRoomState
                          appointmentId={c.appointment_id}
                          token={c.manage_token}
                          sessionStatus={c.consult_session_status}
                          labels={consultStatusLabels}
                        />
                      ) : (
                        c.consult_session_status && (
                          <StatusPill status={c.consult_session_status} labels={consultStatusLabels} />
                        )
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Messages — the client's single thread with the practice (modules/messaging).
              Unconditional, unlike the sections above: an empty thread is not an absent
              feature, it is the invitation to open one, and the composer inside is the
              only place a client can write from their own account. */}
          <Section icon={MessageSquare} title={t("portal.messages")} count={messageCount} testid="client-messages">
            <ClientConversation practiceName={practiceName} onCountChange={setMessageCount} />
          </Section>

          {/* The client's own "news and offers" choice for this practice. Reads its own
              data (GET /client/email-preferences) like PortalPathways, and renders nothing
              when the read is refused (the practice's own people are not its clients). */}
          <PortalEmailPreferences />
        </div>
      </section>
    </ThemedPageLayout>
  );
}
